import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Optional } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { ANALYTICS_DISABLED, AnalyticsSegmentClient } from '@recruitee/analytics';
import { ENVIRONMENT, filterBoolean } from '@recruitee/common';
import { Company, CurrentUser } from '@recruitee/http/types';
import { WindowRef } from '@recruitee/user-interface';

import { installIntercom } from './intercom-lib-loader';
import { IntercomAction, IntercomEnvironment, Recipients } from './types';

declare const Intercom: (action: IntercomAction, data?: any) => void;

@Injectable({
  providedIn: 'root',
})
export class IntercomService {
  public static readonly settings: { hide_default_launcher: boolean; vertical_padding: number } = {
    hide_default_launcher: true,
    vertical_padding: 70,
  };
  public chatOpen$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public unreadCount$: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);
  private ready$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private prodIntercomReady$ = this.analyticsSegmentClient.buffered$.pipe(
    filter(() => this.intercomAvailable),
    take(1),
  );

  constructor(
    @Inject(DOCUMENT) private doc: Document,
    @Inject(ENVIRONMENT) private environment: IntercomEnvironment,
    private windowRef: WindowRef,
    private router: Router,
    private store: Store,
    private analyticsSegmentClient: AnalyticsSegmentClient,
    @Optional() @Inject(ANALYTICS_DISABLED) private analyticsDisabled?: boolean,
  ) {
    this.attachIntercomAPIListeners();
  }

  public open(recipients?: Recipients): void {
    this.intercomAvailable ? Intercom(IntercomAction.Show) : this.openMailClient(recipients);
  }

  public hide(): void {
    if (!this.intercomAvailable) return;

    Intercom(IntercomAction.Hide);
  }

  public update(data?: any): void {
    if (!this.intercomAvailable) return;

    Intercom(IntercomAction.Update, data);
  }

  public startNewMessage(message: string = '', recipients?: Recipients): void {
    this.intercomAvailable
      ? Intercom(IntercomAction.ShowNewMessage, message)
      : this.openMailClient(recipients, message);
  }

  public shutdown(): void {
    if (!this.intercomAvailable) return;

    Intercom(IntercomAction.Shutdown);
  }

  public startSurvey(surveyId: number): void {
    if (!this.intercomAvailable) return;

    Intercom(IntercomAction.startSurvey, surveyId);
  }

  private get intercomAvailable(): boolean {
    /* Intercom is available on production
     * initialized by the segment cdn https://cdn.segment.com/analytics.js
     * Intercom is also available on staging and dynamic environments
     * initialized manually to our testing intercom env
     */
    return typeof Intercom !== 'undefined';
  }

  public openMailClient(recipients?: Recipients, subject: string = ''): void {
    this.windowRef.open(
      `mailto:${recipients ? recipients : Recipients.Support}?subject=${subject}`,
    );
  }

  public init(
    currentCompany: Company,
    currentAdmin: CurrentUser,
    debugEventTracking: boolean,
  ): void {
    if (this.analyticsDisabled) return;

    const { appId } = this.environment.intercom || {};
    if (!!appId && !debugEventTracking) {
      this.initIntercom(appId, currentAdmin);
      this.ready$.next(true);
    } else {
      this.prodIntercomReady$.subscribe(() => {
        this.ready$.next(true);
      });
    }

    if (this.intercomAvailable) {
      const companyLanguage = currentCompany ? currentCompany.language : 'en';
      const preferredLanguage =
        currentAdmin.preferredLanguage !== 'auto'
          ? currentAdmin.preferredLanguage
          : companyLanguage;

      this.update({
        language_override: preferredLanguage,
      });
    }
  }

  private initIntercom(appId: string, currentAdmin: CurrentUser) {
    installIntercom(appId);
    Intercom(IntercomAction.Boot, {
      app_id: appId,
      user_id: currentAdmin.id,
      email: currentAdmin.email,
    });

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.update());
  }
  private attachIntercomAPIListeners(): void {
    this.ready$.pipe(filterBoolean, take(1)).subscribe(ready => {
      Intercom(IntercomAction.onHide, () => {
        this.chatOpen$.next(false);
      });
      Intercom(IntercomAction.onShow, () => {
        this.chatOpen$.next(true);
      });
      Intercom(IntercomAction.onUnreadCountChange, (count: number) => {
        this.unreadCount$.next(count);
      });
    });
  }
}
